/**
 * getvar/setvar bridge — AutoCAD system variable names to app state.
 * Generic numeric variables ride the SystemVars store; ORTHOMODE/OSMODE/
 * CLAYER map to their live subsystems. Unknown names are an honest error
 * (AutoCAD behavior), never a silent nil.
 */

import { LispError } from './errors';
import { mkInt, type LispRuntimeApi, type LispValue } from './values';
import { useSystemVarsStore, type SystemVars } from '../../state/useSystemVarsStore';
import { useAppStore } from '../../state/store';
import { useSnapStore, type SnapType } from '../../tools/snapping/SnapManager';

/**
 * AutoCAD OSMODE bit → snap type. The Faz 7.9 extension adds the three new
 * modes at their REAL AutoCAD bit positions (insertion 64, apparent
 * intersection 1024, geometric center 16384) — the phase note's 16/32 slots
 * are already owned by quadrant/intersection in this table (matching the
 * actual AutoCAD contract), so re-assigning them would corrupt existing
 * scripts. Parallel moves to its correct 4096 bit (was 8192).
 */
const OSMODE_BITS: Array<[number, SnapType]> = [
    [1, 'endpoint'],
    [2, 'midpoint'],
    [4, 'center'],
    [8, 'node'],
    [16, 'quadrant'],
    [32, 'intersection'],
    [64, 'insertion'],
    [128, 'perpendicular'],
    [256, 'tangent'],
    [512, 'nearest'],
    [1024, 'apparentIntersection'],
    [2048, 'extension'],
    [4096, 'parallel'],
    [16384, 'geometricCenter'],
];

function getOsmode(): number {
    const types = useSnapStore.getState().types;
    let bits = 0;
    for (const [bit, type] of OSMODE_BITS) {
        if (types[type]) bits |= bit;
    }
    return bits;
}

function setOsmode(bits: number): void {
    const snap = useSnapStore.getState();
    const enabled: SnapType[] = [];
    for (const [bit, type] of OSMODE_BITS) {
        if (bits & bit) enabled.push(type);
    }
    snap.enableOnly(enabled);
}

function getClayer(): string {
    const app = useAppStore.getState();
    return app.layers[app.activeLayerId]?.name ?? '0';
}

function setClayer(name: string): void {
    const app = useAppStore.getState();
    const target = Object.values(app.layers).find(
        (l) => l.name.toLowerCase() === name.trim().toLowerCase()
    );
    if (!target) throw new LispError(`setvar: no such layer "${name}"`);
    app.setActiveLayer(target.id);
}

function isSystemVarKey(name: string): name is keyof SystemVars {
    return name in useSystemVarsStore.getState();
}

/** Read one system variable as a LISP value. Throws on unknown names. */
export function getSystemVariable(rawName: string): LispValue {
    const name = rawName.trim().toUpperCase();
    const app = useAppStore.getState();
    switch (name) {
        case 'ORTHOMODE':
            return mkInt(app.orthoMode ? 1 : 0);
        case 'OSMODE':
            return mkInt(getOsmode());
        case 'CLAYER':
            return { k: 'str', v: getClayer() };
        case 'FILLETRAD':
        case 'FILLETRADIUS': {
            const v = useSystemVarsStore.getState().getVar('FILLETRADIUS');
            return mkInt(v ?? 0);
        }
        default: {
            if (!isSystemVarKey(name)) {
                throw new LispError(`getvar: unknown system variable "${name}"`);
            }
            const v = useSystemVarsStore.getState().getVar(name);
            return mkInt(v ?? 0);
        }
    }
}

/** Write one system variable. Throws on unknown names / bad values. */
export function setSystemVariable(rawName: string, value: LispValue): void {
    const name = rawName.trim().toUpperCase();
    const numeric =
        value.k === 'int' || value.k === 'real'
            ? Math.trunc(value.v)
            : value.k === 'str'
              ? NaN
              : NaN;
    const app = useAppStore.getState();
    switch (name) {
        case 'ORTHOMODE': {
            const want = numeric !== 0;
            if (app.orthoMode !== want) app.toggleOrthoMode();
            return;
        }
        case 'OSMODE':
            if (!Number.isFinite(numeric)) throw new LispError('setvar: bad argument type');
            setOsmode(numeric);
            return;
        case 'CLAYER':
            if (value.k !== 'str') throw new LispError('setvar: bad argument type');
            setClayer(value.v);
            return;
        case 'FILLETRAD':
        case 'FILLETRADIUS':
            if (!Number.isFinite(numeric)) throw new LispError('setvar: bad argument type');
            useSystemVarsStore.getState().set('FILLETRADIUS', numeric);
            return;
        default: {
            if (!Number.isFinite(numeric) || !isSystemVarKey(name)) {
                throw new LispError(`setvar: unknown system variable "${name}"`);
            }
            useSystemVarsStore.getState().set(name, numeric);
        }
    }
}

/** Install getvar/setvar on the runtime. */
export function registerSysvars(rt: LispRuntimeApi): void {
    rt.globalEnv.define('getvar', {
        k: 'subr',
        name: 'getvar',
        fn: (args) => {
            const name = args[0];
            if (!name || name.k !== 'str') throw new LispError('getvar: bad argument type');
            return getSystemVariable(name.v);
        },
    });
    rt.globalEnv.define('setvar', {
        k: 'subr',
        name: 'setvar',
        fn: (args) => {
            const name = args[0];
            if (!name || name.k !== 'str') throw new LispError('setvar: bad argument type');
            setSystemVariable(name.v, args[1] ?? { k: 'nil' });
            return { k: 'str', v: name.v };
        },
    });
}
